import React, { useState } from "react";
import { Loader2, MessageCircle, MapPin, Instagram, Facebook } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { CONTACT } from "@/i18n/content";
import { Reveal } from "@/components/site/Reveal";
import { createContact } from "@/lib/api";

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const emailValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !emailValid(form.email) || !form.message.trim()) {
      toast.error(t.booking.required);
      return;
    }
    setSending(true);
    try {
      await createContact(form);
      toast.success(t.contact.success);
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error(t.booking.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "rounded-none border-black/15 bg-transparent h-12 focus-visible:ring-1 focus-visible:ring-navy font-manrope";

  return (
    <section id="contact" data-testid="contact-section" className="bg-cream py-16 md:py-24">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <Reveal className="max-w-3xl mb-14">
          <p className="label-mono text-ocean mb-6">{t.contact.label}</p>
          <h2 className="font-outfit font-semibold text-4xl md:text-6xl tracking-tight text-ink">
            {t.contact.title}
          </h2>
          <p className="font-manrope text-ink/60 text-base md:text-lg mt-5 flex items-center gap-2">
            <MapPin size={18} className="text-ocean" /> {t.contact.sub}
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
          {/* Map + CTA */}
          <Reveal>
            <div className="border border-black/10 overflow-hidden aspect-[4/3] grayscale hover:grayscale-0 transition-all duration-500">
              <iframe
                title="Atlas Ziz Tours location"
                data-testid="contact-map"
                src={CONTACT.mapEmbed}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
            <a
              data-testid="contact-whatsapp"
              href={`https://wa.me/${CONTACT.whatsapp}`}
              target="_blank"
              rel="noreferrer"
              className="mt-5 w-full flex items-center justify-center gap-3 bg-[#25D366] text-white label-mono py-4 hover:opacity-90 transition-opacity"
            >
              <MessageCircle size={18} /> {t.contact.whatsapp} · {CONTACT.whatsappDisplay}
            </a>

            <div className="mt-6">
              <p className="label-mono text-ink/50 mb-3">{t.contact.follow}</p>
              <div className="flex items-center gap-3">
                <a
                  data-testid="contact-instagram"
                  href={CONTACT.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center text-white transition-transform hover:scale-110"
                  style={{ background: "linear-gradient(45deg,#f09433,#e6683c,#dc2743,#cc2366,#bc1888)" }}
                  aria-label="Instagram"
                >
                  <Instagram size={19} />
                </a>
                <a
                  data-testid="contact-facebook"
                  href={CONTACT.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 flex items-center justify-center text-white bg-[#1877F2] transition-transform hover:scale-110"
                  aria-label="Facebook"
                >
                  <Facebook size={19} />
                </a>
              </div>
            </div>
          </Reveal>

          {/* Contact form */}
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
              <div>
                <Label className="label-mono text-ink/70">{t.contact.name}</Label>
                <Input
                  data-testid="contact-name"
                  className={inputCls + " mt-2"}
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                />
              </div>
              <div>
                <Label className="label-mono text-ink/70">{t.contact.email}</Label>
                <Input
                  data-testid="contact-email"
                  type="email"
                  className={inputCls + " mt-2"}
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                />
              </div>
              <div>
                <Label className="label-mono text-ink/70">{t.contact.message}</Label>
                <Textarea
                  data-testid="contact-message"
                  rows={5}
                  className="rounded-none border-black/15 bg-transparent mt-2 focus-visible:ring-1 focus-visible:ring-navy font-manrope"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={sending}
                className="w-full bg-navy text-white label-mono py-4 hover:bg-ocean transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {sending && <Loader2 size={16} className="animate-spin" />}
                {sending ? t.contact.sending : t.contact.send}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
